/**
 * Who is signed in, what they may do, and whether their school owns any of it.
 *
 * Entitlements are loaded alongside the profile because almost every screen needs
 * them: `source: 'school'` is what turns a limit from a paywall into a fact. A
 * school student is already paid for, and showing one an upgrade prompt is simply
 * wrong.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { Entitlement, Profile } from '@modulus/api'
import { api, sessionStore } from './api'
import { clearAllSnapshots } from './cache'

interface SessionValue {
  loading: boolean
  isSignedIn: boolean
  profile: Profile | null
  entitlement: Entitlement | null
  userId: string | null
  /** The student's school, when they are in one. Null for a personal account. */
  school: { id: string; name: string } | null
  /** True when the school pays. Nothing may be presented as a paywall. */
  schoolManaged: boolean
  /** Set when the account exists but has not finished choosing a curriculum. */
  needsOnboarding: boolean
  error: unknown
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, displayName: string) => Promise<'signed-in' | 'verify'>
  signOut: () => Promise<void>
  /** Permanently removes the account, then clears every local session snapshot. */
  deleteAccount: () => Promise<void>
  reload: () => void
  /** Re-read the optional billing state after a quota refusal without reloading the app. */
  refreshEntitlement: () => Promise<void>
  setProfile: (profile: Profile) => void
}

const SessionContext = createContext<SessionValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true)
  const [isSignedIn, setIsSignedIn] = useState(() => sessionStore.load() !== null)
  const [profile, setProfileState] = useState<Profile | null>(null)
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [attempt, setAttempt] = useState(0)

  const refreshEntitlement = useCallback(async () => {
    try {
      const ent = await api.learner.entitlements()
      setEntitlement(ent)
    } catch {
      // Billing is deliberately fail-open. Keep the last known entitlement when
      // usage is temporarily unavailable or billing has not been configured.
    }
  }, [])

  const reset = useCallback(() => {
    const userId = sessionStore.load()?.userId
    sessionStore.clear()
    setIsSignedIn(false)
    setProfileState(null)
    setEntitlement(null)
    if (userId) void clearAllSnapshots(userId)
  }, [])

  useEffect(() => {
    api.client.onSessionLost(reset)
    return () => api.client.onSessionLost(null)
  }, [reset])

  useEffect(() => {
    if (!isSignedIn) {
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    void (async () => {
      try {
        const me = await api.learner.profile()
        if (cancelled) return
        setProfileState(me)

        // Entitlements must never block the app. If the call fails the student
        // gets the conservative default — everything visible, limits enforced by
        // the server anyway — rather than a broken screen.
        try {
          const ent = await api.learner.entitlements()
          if (!cancelled) setEntitlement(ent)
        } catch {
          if (!cancelled) setEntitlement(null)
        }
      } catch (caught) {
        if (!cancelled) setError(caught)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isSignedIn, attempt])

  const value = useMemo<SessionValue>(() => {
    const learner = profile?.learner ?? null
    const school = learner?.scope === 'school' ? (learner.school ?? null) : null

    return {
      loading,
      isSignedIn,
      profile,
      entitlement,
      userId: profile?.id ?? sessionStore.load()?.userId ?? null,
      school,
      schoolManaged: entitlement?.source === 'school' || school !== null,
      // A brand-new account has no curriculum selected. Everything else in the app
      // reads from one, so this is the gate rather than a nag.
      needsOnboarding: profile !== null && !profile.default_curriculum_uuid,
      error,
      refreshEntitlement,
      signIn: async (email, password) => {
        await api.auth.login(email, password)
        setIsSignedIn(true)
        setAttempt((n) => n + 1)
      },
      signUp: async (email, password, displayName) => {
        const result = await api.auth.signUp(email, password, displayName)
        if ('email_verification_required' in result) return 'verify'
        setIsSignedIn(true)
        setAttempt((n) => n + 1)
        return 'signed-in'
      },
      signOut: async () => {
        await api.auth.logout()
        reset()
      },
      deleteAccount: async () => {
        await api.learner.deleteAccount()
        reset()
      },
      reload: () => setAttempt((n) => n + 1),
      setProfile: setProfileState,
    }
  }, [loading, isSignedIn, profile, entitlement, error, refreshEntitlement, reset])

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext)
  if (!value) throw new Error('useSession must be used inside a SessionProvider')
  return value
}
